import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import logger from '../lib/logger';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { submitContactForm } from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Mail,
  User, 
  MessageSquare, 
  Send, 
  Loader2, 
  CheckCircle2,
  ArrowLeft,
  Globe,
  Clock,
  Shield
} from 'lucide-react';

// reCAPTCHA Site Key - should be in environment variable in production
const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key for development
const RECAPTCHA_LOAD_TIMEOUT = 5000; // 5 seconds timeout for reCAPTCHA

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaFailed, setRecaptchaFailed] = useState(false);

  // Load reCAPTCHA script with graceful fallback for Huawei/non-GMS devices
  useEffect(() => {
    let timeoutId;
    let isMounted = true;
    
    const loadRecaptcha = () => {
      try {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          if (isMounted) {
            setRecaptchaLoaded(true);
            clearTimeout(timeoutId);
          }
        };
        
        script.onerror = () => {
          if (isMounted) {
            console.warn('[Contact] reCAPTCHA failed to load - using fallback (Huawei/GMS unavailable)');
            setRecaptchaFailed(true);
            clearTimeout(timeoutId);
          }
        };
        
        document.head.appendChild(script);
        
        // Timeout fallback - if reCAPTCHA doesn't load in 5 seconds, allow form anyway
        timeoutId = setTimeout(() => {
          if (isMounted && !recaptchaLoaded) {
            console.warn('[Contact] reCAPTCHA load timeout - using fallback');
            setRecaptchaFailed(true);
          }
        }, RECAPTCHA_LOAD_TIMEOUT);
        
      } catch (error) {
        console.warn('[Contact] Error loading reCAPTCHA:', error);
        if (isMounted) {
          setRecaptchaFailed(true);
        }
      }
    };
    
    loadRecaptcha();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      // Cleanup
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        existingScript.remove();
      }
      // Remove reCAPTCHA badge
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        badge.remove();
      }
    };
  }, []);

  const executeRecaptcha = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Graceful fallback for Huawei/non-GMS devices
      if (recaptchaFailed || !window.grecaptcha) {
        console.warn('[Contact] reCAPTCHA unavailable, using fallback token');
        resolve('huawei-fallback-token');
        return;
      }
      
      try {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action: 'contact_form' })
            .then(resolve)
            .catch((error) => {
              console.warn('[Contact] reCAPTCHA execution failed:', error);
              resolve('huawei-fallback-token');
            });
        });
      } catch (error) {
        console.warn('[Contact] reCAPTCHA error:', error);
        resolve('huawei-fallback-token');
      }
    });
  }, [recaptchaFailed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      toast.error('Please enter a message (minimum 10 characters)');
      return;
    }
    
    setLoading(true);
    
    try {
      // Get reCAPTCHA token
      let recaptchaToken = '';
      try {
        recaptchaToken = await executeRecaptcha();
      } catch (recaptchaError) {
        logger.error('reCAPTCHA error:', recaptchaError);
        // Continue with empty token for development
        recaptchaToken = 'development-token';
      }
      
      const response = await submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
        recaptcha_token: recaptchaToken
      });
      
      setSubmissionId(response.data.submission_id);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({ name: '', email: '', message: '' });
    setSubmitted(false);
    setSubmissionId('');
  };

  return (
    <>
      <Helmet>
        <title>Contact ParachPay | ASELU Investment Growth Limited</title>
        <meta name="description" content="Contact ParachPay - ASELU Investment Growth Limited (RC1947196). Reach us at info@parachpay.com. Offices in Nigeria and Netherlands." />
        <link rel="canonical" href="https://parachpay.com/contact" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-emerald-500/5">
        {/* Header */}
        <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-xl font-bold font-heading">
              <img 
                src="/images/parachpay-logo.jpg" 
                alt="ParachPay Logo" 
                width="32"
                height="32"
                className="h-8 w-auto rounded-lg"
              />
              ParachPay
            </Link>
            <Link to="/">
              <Button variant="ghost" className="rounded-xl">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Page Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4">
                Get in <span className="text-emerald-500">Touch</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Have a question or need assistance? We'd love to hear from you. Fill out the form below and our team will get back to you shortly.
              </p>
            </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="md:col-span-1 space-y-6"
            >
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
                <CardContent className="pt-6 space-y-6">
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-4">Contact Information</h3>
                    <p className="text-white/80 text-sm">
                      Fill out the form and our team will get back to you as soon as possible.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 mt-0.5" />
                      <div>
                        <p className="font-medium">Email</p>
                        <a 
                          href="mailto:info@parachpay.com" 
                          className="text-sm text-white/80 hover:text-white transition-colors"
                        >
                          info@parachpay.com
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 mt-0.5" />
                      <div>
                        <p className="font-medium">Business Hours</p>
                        <p className="text-sm text-white/80">
                          24/7 - We're always here to help
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 mt-0.5" />
                      <div>
                        <p className="font-medium">Registered office (Nigeria)</p>
                        <p className="text-sm text-white/80">
                          67 Siluko Road,<br />
                          Benin City, Nigeria
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 mt-0.5" />
                      <div>
                        <p className="font-medium">European operations (Netherlands)</p>
                        <p className="text-sm text-white/80">
                          Rudolf Steinerstraat 32,<br />
                          2037 GC Haarlem, Netherlands
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/20 space-y-2">
                    <p className="text-xs text-white/70">
                      <strong>Legal name:</strong> ASELU Investment Growth Limited
                    </p>
                    <p className="text-xs text-white/70">
                      <strong>Registration:</strong> RC1947196 (Nigeria)
                    </p>
                    <div className="flex items-center gap-2 pt-2">
                      <Globe className="w-4 h-4" />
                      <a 
                        href="https://parachpay.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm hover:underline"
                      >
                        www.parachpay.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Support */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-5 h-5 text-emerald-500" />
                    <h3 className="font-heading font-semibold">Need Immediate Help?</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Log in to your account and chat with Deborah, our AI support assistant, for instant answers.
                  </p>
                  <Link to="/login">
                    <Button variant="outline" className="w-full rounded-xl">
                      Login to Chat with Deborah
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="md:col-span-2"
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Send us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      </div>
                      <h3 className="text-xl font-heading font-semibold mb-2">Message Sent!</h3>
                      <p className="text-muted-foreground mb-2">
                        Thank you for contacting us. We'll get back to you soon.
                      </p>
                      <p className="text-sm text-muted-foreground mb-6">
                        Your reference ID: <span className="font-mono font-medium text-emerald-600">{submissionId}</span>
                      </p>
                      <div className="flex justify-center gap-4">
                        <Button onClick={handleReset} variant="outline" className="rounded-xl">
                          Send Another Message
                        </Button>
                        <Link to="/">
                          <Button className="rounded-xl">
                            Back to Home
                          </Button>
                        </Link>
                      </div>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name Field */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Your Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="rounded-xl"
                          data-testid="contact-name-input"
                        />
                      </div>

                      {/* Email Field */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="rounded-xl"
                          data-testid="contact-email-input"
                        />
                      </div>

                      {/* Message Field */}
                      <div className="space-y-2">
                        <Label htmlFor="message" className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Your Message <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="message"
                          placeholder="How can we help you today?"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          rows={6}
                          className="rounded-xl resize-none"
                          data-testid="contact-message-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          {formData.message.length}/5000 characters
                        </p>
                      </div>

                      {/* reCAPTCHA Notice */}
                      <div className="p-4 rounded-xl bg-muted/50 text-sm text-muted-foreground">
                        <p>
                          This site is protected by reCAPTCHA and the Google{' '}
                          <a 
                            href="https://policies.google.com/privacy" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:underline"
                          >
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a 
                            href="https://policies.google.com/terms" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:underline"
                          >
                            Terms of Service
                          </a>{' '}
                          apply.
                        </p>
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading || !recaptchaLoaded}
                        className="w-full rounded-xl h-12"
                        data-testid="contact-submit-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background mt-12">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-emerald-600">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-emerald-600">
                Terms & Conditions
              </Link>
              <a 
                href="https://parachpay.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-emerald-600"
              >
                About Us
              </a>
            </div>
            <div className="text-sm text-muted-foreground text-center md:text-right">
              <p>© 2026 ParachPay. All rights reserved.</p>
              <p className="text-xs mt-1">ASELU Investment Growth Limited · Registered in Nigeria · RC1947196</p>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

export default Contact;
